import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { FilteredState, PaginationParams, SortParams, FilterParams } from '../types';
import { appConfig } from '@/config/app-config';
export interface InvoiceConcept {
  id: number;
  code: string;
  name: string;
  description: string | null;
  fee_code: string | null;
  carrier_id: number | null;
  container_type: string | null;
  is_active: boolean;
  created_at: string;
  created_by: string | null;
  updated_at: string | null;
  updated_by: string | null;
}
// Interfaz para opciones de filtro (usado en dropdowns)
export interface InvoiceConceptFilterOption {
  value: string;
  label: string;
  code?: string;
  fee_code?: string;
  container_type?: string;
}
interface InvoiceConceptsState extends FilteredState<InvoiceConcept> {
  selectedConcept: InvoiceConcept | null;
  creating: boolean;
  updating: boolean;
  deleting: boolean;
  // Estado para opciones de filtro (cacheadas para evitar recargas)
  filterOptions: InvoiceConceptFilterOption[];
  filterOptionsLoading: boolean;
  filterOptionsError: string | null;
  filterOptionsLastFetch: number | null;
}
const initialState: InvoiceConceptsState = {
  items: [],
  totalCount: 0,
  currentPage: 1,
  pageSize: 50,
  loading: false,
  error: null,
  lastFetch: null,
  filters: {},
  sortField: 'code',
  sortDirection: 'asc',
  searchTerm: null,
  selectedConcept: null,
  creating: false,
  updating: false,
  deleting: false,
  // Estado para opciones de filtro
  filterOptions: [],
  filterOptionsLoading: false,
  filterOptionsError: null,
  filterOptionsLastFetch: null,
};
// Thunk para cargar invoice concepts con paginación y filtros (para vistas de lista)
export const fetchInvoiceConcepts = createAsyncThunk<{ data: InvoiceConcept[]; totalCount: number }, any>(
  'invoiceConcepts/fetchConcepts',
  async (params: any) => {
    throw new Error('Not implemented yet');
  }
);
// Thunk para cargar opciones de filtro (solo id, código, nombre) - CACHEADO
export const fetchInvoiceConceptFilterOptions = createAsyncThunk(
  'invoiceConcepts/fetchFilterOptions',
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as any;
    const invoiceConceptsState = state.invoiceConcepts as InvoiceConceptsState;
    // ✅ CACHE: Si ya tenemos datos frescos (menos de 5 minutos), usar cache
    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos
    if (
      invoiceConceptsState.filterOptionsLastFetch &&
      Date.now() - invoiceConceptsState.filterOptionsLastFetch < CACHE_DURATION &&
      invoiceConceptsState.filterOptions.length > 0
    ) {
      return invoiceConceptsState.filterOptions;
    }
    try {
      // Obtener token de autenticación
      const token = localStorage.getItem('authToken') || '';
      const url = appConfig.getApiUrl('masters/invoice-concepts?startRow=0&endRow=500&sortProperty=code&sortDirection=asc');
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(`Error ${response.status}: ${errorText}`);
      }
      const result = await response.json();
      const invoiceConcepts = result?.data?.rows || [];
      // Transformar a formato de opciones de filtro
      const filterOptions: InvoiceConceptFilterOption[] = invoiceConcepts.map((concept: any) => ({
        value: concept.id.toString(),
        label: `${concept.code} - ${concept.name}`,
        code: concept.code,
        fee_code: concept.fee_code,
        container_type: concept.container_type
      }));
      return filterOptions;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Error al cargar opciones de filtro');
    }
  }
);
export const fetchInvoiceConceptById = createAsyncThunk('invoiceConcepts/fetchConceptById', async (id: number) => {
  throw new Error('Not implemented yet');
});
export const createInvoiceConcept = createAsyncThunk('invoiceConcepts/createConcept', async (concept: Partial<InvoiceConcept>) => {
  throw new Error('Not implemented yet');
});
export const updateInvoiceConcept = createAsyncThunk('invoiceConcepts/updateConcept', async ({ id, data }: { id: number; data: Partial<InvoiceConcept> }) => {
  throw new Error('Not implemented yet');
});
export const deleteInvoiceConcept = createAsyncThunk('invoiceConcepts/deleteConcept', async (id: number) => {
  throw new Error('Not implemented yet');
});
const invoiceConceptsSlice = createSlice({
  name: 'invoiceConcepts',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<FilterParams>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.currentPage = 1;
    },
    clearFilters: (state) => {
      state.filters = {};
      state.searchTerm = null;
      state.currentPage = 1;
    },
    setSearchTerm: (state, action: PayloadAction<string | null>) => {
      state.searchTerm = action.payload;
      state.currentPage = 1;
    },
    setSort: (state, action: PayloadAction<SortParams>) => {
      state.sortField = action.payload.field;
      state.sortDirection = action.payload.direction;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.currentPage = 1;
    },
    selectConcept: (state, action: PayloadAction<InvoiceConcept | null>) => {
      state.selectedConcept = action.payload;
    },
    clearSelection: (state) => {
      state.selectedConcept = null;
    },
  },
  extraReducers: (builder) => {
    // Casos para fetchInvoiceConcepts (lista completa)
    builder
      .addCase(fetchInvoiceConcepts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInvoiceConcepts.fulfilled, (state, action: PayloadAction<{ data: InvoiceConcept[]; totalCount: number }>) => {
        state.loading = false;
        state.items = action.payload.data;
        state.totalCount = action.payload.totalCount;
        state.lastFetch = Date.now();
      })
      .addCase(fetchInvoiceConcepts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Error al cargar conceptos de factura';
      })
      // Casos para fetchInvoiceConceptFilterOptions (opciones cacheadas)
      .addCase(fetchInvoiceConceptFilterOptions.pending, (state) => {
        state.filterOptionsLoading = true;
        state.filterOptionsError = null;
      })
      .addCase(fetchInvoiceConceptFilterOptions.fulfilled, (state, action) => {
        state.filterOptionsLoading = false;
        state.filterOptions = action.payload;
        state.filterOptionsLastFetch = Date.now();
      })
      .addCase(fetchInvoiceConceptFilterOptions.rejected, (state, action) => {
        state.filterOptionsLoading = false;
        state.filterOptionsError = action.payload as string || 'Error al cargar opciones de filtro';
      });
  },
});
// Selectores para acceder al estado
export const selectInvoiceConceptFilterOptions = (state: any) => state.invoiceConcepts.filterOptions;
export const selectInvoiceConceptFilterOptionsLoading = (state: any) => state.invoiceConcepts.filterOptionsLoading;
export const selectInvoiceConceptFilterOptionsError = (state: any) => state.invoiceConcepts.filterOptionsError;
// Helper para verificar si las opciones de filtro están frescas
export const areInvoiceConceptFilterOptionsFresh = (state: any): boolean => {
  const lastFetch = state.invoiceConcepts.filterOptionsLastFetch;
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos
  return lastFetch && Date.now() - lastFetch < CACHE_DURATION;
};
export const { setFilters, clearFilters, setSearchTerm, setSort, setPage, setPageSize, selectConcept, clearSelection } = invoiceConceptsSlice.actions;
export default invoiceConceptsSlice.reducer;
